import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { classes } from "./tokens";
import { fetchCarserviceStats } from "../../services/publicCrmApi";
import { useCarServiceCity } from "./CarServiceCityContext";

/**
 * Stats Strip — gomechanic-style dark counter row.
 * Pulls live counts from CRM /carservice/stats with a 5-min client cache;
 * falls back to seeded numbers so the section always renders.
 *
 * Field mapping (CRM /carservice/stats → tile):
 *   total_serviced  →  "Cars Serviced"
 *   review_count    →  "Verified Reviews"          (Google + Trustpilot)
 *   rating          →  "Google Rating"             (sublabel: review_count)
 *   city_count      →  "Cities Covered"
 *   today_bookings  →  "X bookings in {city} today" header microcopy
 *   warranty_km     →  warranty number used elsewhere on LP (not in this strip)
 */
const FALLBACK_STATS = {
  total_serviced: "5,000+",
  review_count:   "3,200+",
  rating:         "4.8★",
  city_count:     "500+",
};

function fmtNumber(n) {
  if (n == null) return null;
  if (typeof n === "string") return n;
  if (n >= 10000000) return `${(n / 10000000).toFixed(1).replace(/\.0$/, "")} Cr+`;
  if (n >= 100000)   return `${(n / 100000).toFixed(1).replace(/\.0$/, "")} L+`;
  if (n >= 1000)     return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k+`;
  return `${n.toLocaleString("en-IN")}+`;
}

const CarServiceStatsStrip = () => {
  const [live, setLive] = useState(null);
  const { selectedCity } = useCarServiceCity();
  const cityName = selectedCity?.name || null;

  useEffect(() => {
    // Only fetch city-scoped stats once the user has picked a city. Before
    // that, we show national fallback numbers (no city-specific bookings
    // line) so we never contradict the header's "Select your city" state.
    if (!cityName) {
      setLive(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCarserviceStats(cityName);
        if (!cancelled) setLive(data);
      } catch (_) {
        // Stay on fallback — we don't surface API failures on this section.
      }
    })();
    return () => { cancelled = true; };
  }, [cityName]);

  const stats = [
    {
      value: fmtNumber(live?.total_serviced) || FALLBACK_STATS.total_serviced,
      label: "Cars Serviced",
      sublabel: "across India",
    },
    {
      value: fmtNumber(live?.review_count) || FALLBACK_STATS.review_count,
      label: "Verified Reviews",
      sublabel: "Google + Trustpilot",
    },
    {
      value: live?.rating ? `${live.rating}★` : FALLBACK_STATS.rating,
      label: "Google Rating",
      sublabel: "real customer ratings",
    },
    {
      value: fmtNumber(live?.city_count) || FALLBACK_STATS.city_count,
      label: "Cities Covered",
      sublabel: "pan-India",
    },
  ];

  const ratingNumber = live?.rating || 4.8;

  return (
    <section
      className="relative py-16 lg:py-20 bg-slate-950 text-white overflow-hidden"
      data-testid="stats-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-3xl" />
      </div>

      <div className={`${classes.container} relative`}>
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
            By The Numbers
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-extrabold mt-2">
            Trusted by thousands of car owners across India
          </h2>
          {live?.today_bookings != null ? (
            <p
              className="mt-3 text-sm text-blue-200 font-semibold"
              data-testid="stats-today-bookings"
            >
              {live.today_bookings} bookings in {live.city || cityName} today
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="text-center"
              data-testid={`stat-${i}`}
            >
              <div className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight bg-gradient-to-br from-white to-slate-400 bg-clip-text text-transparent">
                {s.value}
              </div>
              <div className="mt-2 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
                {s.label}
              </div>
              {s.sublabel && (
                <div className="mt-0.5 text-[10px] text-slate-500 font-semibold">
                  {s.sublabel}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-3 text-sm">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-4 w-4 text-blue-400 fill-blue-400" />
          ))}
          <span className="text-slate-300 font-semibold">
            {ratingNumber} / 5 average across Google, Facebook &amp; Trustpilot
          </span>
        </div>
      </div>
    </section>
  );
};

export default CarServiceStatsStrip;
